// Fleet tracker — vehicles, documents, maintenance schedules, service history.
//
// Dylan 2026-07-16. Drag a BC registration onto the drop zone to add a van;
// Claude reads it and pre-fills the form (every field stays editable — the
// extraction is a first draft, not an answer). Per-vehicle: who's responsible
// and where it sits, the finance/insurance numbers Dylan fills in by hand, a
// manufacturer-recommended maintenance schedule, and the service history the
// expense scan pulls out of invoices we already ingest.
//
// Inline styles only (project rule — no CSS modules / Tailwind).

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";

// Documents are uploaded/read/deleted through api/fleet.js with the service
// role — the browser never touches the fleet-docs bucket (it can't; the bucket
// is private and has no storage policy). See api/fleet.js storageUpload().

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  wrap:      { padding: "18px 16px 80px", maxWidth: 1180, margin: "0 auto" },
  h1:        { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 },
  sub:       { fontSize: 13, color: "#64748b", marginTop: 3 },
  card:      { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 14 },
  sectionH:  { fontSize: 13, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 },
  label:     { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4, display: "block" },
  input:     { width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, boxSizing: "border-box", background: "#fff" },
  grid:      (n = 3) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${n === 2 ? 240 : 160}px, 1fr))`, gap: 10 }),
  btn:       { padding: "8px 14px", borderRadius: 7, border: "1px solid #cbd5e1", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#0f172a" },
  btnPri:    { padding: "8px 14px", borderRadius: 7, border: "none", background: "#a5ce3a", color: "#1a2e05", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  btnGhost:  { padding: "4px 9px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer", color: "#475569" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:        { textAlign: "left", padding: "7px 8px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" },
  td:        { padding: "7px 8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  err:       { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "9px 12px", borderRadius: 7, fontSize: 13, marginBottom: 12 },
  note:      { background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569", padding: "9px 12px", borderRadius: 7, fontSize: 12.5 },
};

const CHIP = {
  overdue:  { bg: "#fee2e2", fg: "#991b1b", bd: "#fecaca", label: "Overdue" },
  due:      { bg: "#ffedd5", fg: "#9a3412", bd: "#fed7aa", label: "Due now" },
  due_soon: { bg: "#fef9c3", fg: "#854d0e", bd: "#fde68a", label: "Due soon" },
  ok:       { bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0", label: "OK" },
  unknown:  { bg: "#f1f5f9", fg: "#475569", bd: "#e2e8f0", label: "No baseline" },
};

function Chip({ status, children }) {
  const c = CHIP[status] || CHIP.unknown;
  return (
    <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: "nowrap" }}>
      {children || c.label}
    </span>
  );
}

// Ford gates its "special operating conditions" on operating PRIMARILY in a
// condition, and each one has a different effect — this is not a severe/normal
// switch. The wording here is deliberately close to Ford's own so the person
// ticking the box is answering the question Ford actually asks.
const DUTY_OPTIONS = [
  { key: "heavy_commercial", label: "Heavy commercial use", hint: "Extensive idling or low-speed driving over long distances (delivery, service calls). Shortens spark plugs 160,000 → 96,000 km." },
  { key: "dusty", label: "Dusty or sandy conditions", hint: "Unpaved, dusty or gravel roads. The only condition that replaces the oil-life monitor with a fixed 8,000 km / 6 month oil change." },
  { key: "towing", label: "Towing a trailer", hint: "Shortens spark plugs to 96,000 km and adds U-joint lube every 96,000 km." },
];

function DutyConditions({ value, onChange }) {
  const set = new Set(value || []);
  const toggle = (k) => {
    const next = new Set(set);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange([...next]);
  };
  return (
    <div>
      <label style={S.label}>Operating conditions</label>
      <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 8 }}>
        Ford publishes a different schedule for each of these. Only tick what the van does <em>primarily</em> — occasional doesn't count.
      </div>
      {DUTY_OPTIONS.map((o) => (
        <label key={o.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={set.has(o.key)} onChange={() => toggle(o.key)} style={{ marginTop: 3 }} />
          <span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{o.label}</span>
            <span style={{ display: "block", fontSize: 11.5, color: "#64748b" }}>{o.hint}</span>
          </span>
        </label>
      ))}
      <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
        Changing these doesn’t rewrite an existing schedule — use “Re-seed from template” after saving.
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

/** A list of things the system checked and wants a human to look at. */
function IssueList({ issues, tone = "warn" }) {
  if (!issues?.length) return null;
  const c = tone === "warn"
    ? { bg: "#fffbeb", bd: "#fde68a", fg: "#854d0e" }
    : { bg: "#fef2f2", bd: "#fecaca", fg: "#991b1b" };
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, borderRadius: 7, padding: "9px 12px", fontSize: 12.5, marginTop: 10 }}>
      {issues.map((t, i) => <div key={i} style={{ marginTop: i ? 6 : 0 }}>⚠ {t}</div>)}
    </div>
  );
}

/**
 * VIN input with a Decode button.
 *
 * Exists because the first van was saved with a 16-character VIN read off a
 * photo — nothing checked it, and NHTSA garbage-decodes it to a 1995 diesel.
 * Decoding also fills what a registration simply doesn't print (engine,
 * drivetrain) and gives the CANONICAL model name.
 */
function VinField({ value, onChange, year, onDecoded }) {
  const [state, setState] = useState({ busy: false, errors: [], warnings: [], fields: null });

  const decode = async () => {
    if (!value) return;
    setState({ busy: true, errors: [], warnings: [], fields: null });
    try {
      const j = await callFleet({ action: "decodeVin", vin: value, year: num(year) });
      setState({ busy: false, errors: j.errors || [], warnings: j.warnings || [], fields: j.fields || null });
      if (j.ok && j.fields) onDecoded?.(j.fields, j.vin);
    } catch (e) {
      setState({ busy: false, errors: [e.message], warnings: [], fields: null });
    }
  };

  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <label style={S.label}>VIN</label>
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...S.input, fontFamily: "ui-monospace, monospace", letterSpacing: 0.4 }}
               value={value || ""} onChange={onChange} placeholder="17 characters" maxLength={20} />
        <button style={{ ...S.btn, whiteSpace: "nowrap" }} onClick={decode} disabled={state.busy || !value} type="button">
          {state.busy ? "…" : "Decode"}
        </button>
      </div>
      <div style={{ fontSize: 11, color: (value && value.length !== 17) ? "#b45309" : "#94a3b8", marginTop: 3 }}>
        {value ? `${value.length} / 17 characters` : "A VIN is always 17 characters"}
        {" · Decode fills engine, drivetrain and the proper model name from NHTSA"}
      </div>
      <IssueList issues={state.errors} tone="err" />
      <IssueList issues={state.warnings} />
      {state.fields && !state.errors.length && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", borderRadius: 7, padding: "8px 11px", fontSize: 12.5, marginTop: 10 }}>
          ✓ {[state.fields.year, state.fields.make, state.fields.model, state.fields.series].filter(Boolean).join(" ")}
          {state.fields.body_style ? ` · ${state.fields.body_style}` : ""}
          {state.fields.engine ? ` · ${state.fields.engine}` : ""}
          {state.fields.drivetrain ? ` · ${state.fields.drivetrain}` : ""}
          {state.fields.gvw_kg ? ` · GVWR ${state.fields.gvw_kg} kg` : ""}
        </div>
      )}
    </div>
  );
}

/**
 * Crew picker for the responsible employee.
 * If the assigned address isn't in the active crew list (they left, or the crew
 * list hasn't loaded), a plain <select> silently renders as "— unassigned —"
 * and the next save would WIPE the assignment. So carry the current value as
 * its own option instead of dropping it on the floor.
 */
function AssigneeSelect({ value, crew, onChange }) {
  const known = crew.some((c) => c.email === value);
  return (
    <select style={S.input} value={value || ""} onChange={onChange}>
      <option value="">— unassigned —</option>
      {value && !known && <option value={value}>{value.split("@")[0]} (not on the active crew list)</option>}
      {crew.map((c) => <option key={c.email} value={c.email}>{c.display_name || c.email}</option>)}
    </select>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
const num = (v) => (v === "" || v == null ? null : Number(v));
const fmtKm = (v) => (v == null ? "—" : `${Number(v).toLocaleString()} km`);
const fmt$ = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const vehName = (v) => [v.unit_name, [v.year, v.make, v.model].filter(Boolean).join(" ")].filter(Boolean).join(" — ") || "Untitled vehicle";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result.split(",")[1]);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

async function callFleet(payload) {
  const r = await apiFetch("/api/fleet", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  if (j.ok === false && j.error) throw new Error(j.error);
  return j;
}

// ── Drop zone ──────────────────────────────────────────────────────────────
function DropZone({ onFiles, busy, label, hint, accept }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) onFiles([...e.dataTransfer.files]); }}
      onClick={() => !busy && ref.current?.click()}
      style={{
        border: `2px dashed ${over ? "#a5ce3a" : "#cbd5e1"}`,
        background: over ? "#f7fee7" : "#fafafa",
        borderRadius: 10, padding: "22px 16px", textAlign: "center",
        cursor: busy ? "wait" : "pointer", transition: "all .12s",
      }}
    >
      <input ref={ref} type="file" multiple accept={accept || "image/*,.pdf,application/pdf"} style={{ display: "none" }}
             onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} />
      <div style={{ fontSize: 24, marginBottom: 6 }}>{busy ? "⏳" : "📄"}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{busy ? "Reading…" : label}</div>
      {hint && <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Vehicle list
// ══════════════════════════════════════════════════════════════════════════
function VehicleCard({ v, onOpen }) {
  const { isAdmin, isOwner } = usePermissions() || {};
  const showMoney = isAdmin || isOwner;
  const c = v.counts || {};
  const bad = (c.overdue || 0) + (c.due || 0);
  return (
    <div onClick={onOpen} style={{ ...S.card, marginBottom: 0, cursor: "pointer", borderColor: c.overdue ? "#fecaca" : "#e2e8f0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{vehName(v)}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {v.plate ? `Plate ${v.plate}` : "No plate on file"}
            {v.status !== "active" && ` · ${v.status}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {c.overdue > 0 && <Chip status="overdue">{c.overdue} overdue</Chip>}
          {c.due > 0 && <Chip status="due">{c.due} due</Chip>}
          {!bad && c.due_soon > 0 && <Chip status="due_soon">{c.due_soon} soon</Chip>}
          {!bad && !c.due_soon && v.status === "active" && <Chip status="ok">Up to date</Chip>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: 11, flexWrap: "wrap", fontSize: 12.5, color: "#475569" }}>
        <div>
          <span style={{ color: "#94a3b8" }}>Odometer </span>
          {v.odo?.km == null ? "—" : (
            <strong>{fmtKm(v.odo.km)}{v.odo.estimated && <span style={{ color: "#94a3b8", fontWeight: 400 }}> est.</span>}</strong>
          )}
        </div>
        <div><span style={{ color: "#94a3b8" }}>With </span><strong>{v.assigned_to_email ? v.assigned_to_email.split("@")[0] : "unassigned"}</strong></div>
        {showMoney && v.monthly_payment != null && <div><span style={{ color: "#94a3b8" }}>Payment </span><strong>{fmt$(v.monthly_payment)}/mo</strong></div>}
        {showMoney && v.is_paid_off && <div style={{ color: "#166534", fontWeight: 700 }}>Paid off</div>}
      </div>

      {c.unknown > 0 && (
        <div style={{ fontSize: 11.5, color: "#854d0e", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 8px", marginTop: 9 }}>
          {c.unknown} service{c.unknown > 1 ? "s" : ""} have no baseline yet — set the last-done date/km so they can be tracked.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Add vehicle (drag a registration in)
// ══════════════════════════════════════════════════════════════════════════
const BLANK = {
  unit_name: "", status: "active", year: "", make: "", model: "", body_style: "", colour: "",
  vin: "", plate: "", registration_number: "", certificate_number: "", vic: "", fuel_type: "",
  net_weight_kg: "", gvw_kg: "", seating_capacity: "", vehicle_type: "", registration_expiry: "",
  assigned_to_email: "", home_address: "", odometer_km: "", default_km_per_year: 25000,
  ownership_type: "", purchase_date: "", purchase_price: "", monthly_payment: "", interest_rate: "",
  term_months: "", payments_start_date: "", payoff_date: "", buyout_amount: "", lender: "", is_paid_off: false,
  insurer: "", policy_number: "", insurance_monthly: "", insurance_renewal: "",
  qb_account_hint: "", match_aliases: "", duty_conditions: ["heavy_commercial"], notes: "",
  engine: "", drivetrain: "",
};

function AddVehicle({ crew, onSaved, onCancel }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [extractNote, setExtractNote] = useState(null);
  const [issues, setIssues] = useState([]);
  const [pendingDoc, setPendingDoc] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onFiles = async (files) => {
    const file = files[0];
    if (!file) return;
    setBusy(true); setErr(null); setExtractNote(null);
    try {
      const b64 = await readFileAsBase64(file);
      const j = await callFleet({ action: "extractDoc", fileBase64: b64, mimeType: file.type, fileName: file.name });
      if (!j.ok) throw new Error(j.error || "extraction failed");
      const p = j.parsed || {};
      const v = p.vehicle || {};

      // Only fill blanks — never clobber something already typed.
      setForm((f) => {
        const next = { ...f };
        for (const [k, val] of Object.entries(v)) {
          if (val != null && val !== "" && (next[k] === "" || next[k] == null)) next[k] = val;
        }
        if (p.insurance) for (const [k, val] of Object.entries(p.insurance)) {
          if (val != null && val !== "" && (next[k] === "" || next[k] == null)) next[k] = val;
        }
        // The QB chart books repairs under a "<Make> <Model>" sub-account
        // ("Truck Expenses - Ford Transit (2018)"), so pre-seed the matcher
        // hint — but from the VIN-DECODED model, never the registration's
        // abbreviation. A hint of "Ford Trnst" would never match "Ford Transit"
        // and the van's invoices would silently never attribute.
        const dec = p.vin_decoded || {};
        const make = dec.make || v.make;
        const model = dec.model || v.model;
        if (!next.qb_account_hint && make && model) next.qb_account_hint = `${make} ${model}`;
        if (!next.unit_name && make && model) next.unit_name = `${make} ${model}`.trim();
        return next;
      });

      setPendingDoc({ file, base64: b64, parsed: p });
      // p.issues are the server's CHECKS (bad VIN, expiry in the past), not the
      // model's own musings — they matter more than confidence_notes.
      setIssues(p.issues || []);
      setExtractNote(
        (p.confidence_notes && String(p.confidence_notes).trim())
          ? `Read as ${p.doc_type || "document"}. Model flagged: ${p.confidence_notes}`
          : `Read as ${p.doc_type || "document"}. Check every field before saving — this is Claude reading a photo, not a database.`
      );
    } catch (e) {
      setErr(`Couldn't read that document: ${e.message}. You can still fill the form in by hand.`);
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        action: "saveVehicle", ...form,
        year: num(form.year), net_weight_kg: num(form.net_weight_kg), gvw_kg: num(form.gvw_kg),
        seating_capacity: num(form.seating_capacity), odometer_km: num(form.odometer_km),
        default_km_per_year: num(form.default_km_per_year), purchase_price: num(form.purchase_price),
        monthly_payment: num(form.monthly_payment), interest_rate: num(form.interest_rate),
        term_months: num(form.term_months), buyout_amount: num(form.buyout_amount),
        insurance_monthly: num(form.insurance_monthly),
        match_aliases: String(form.match_aliases || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      for (const k of ["registration_expiry", "purchase_date", "payments_start_date", "payoff_date", "insurance_renewal"]) {
        if (!body[k]) body[k] = null;
      }
      const j = await callFleet(body);
      if (!j.vehicle) throw new Error("save returned no vehicle");

      // Keep the document we read the details out of, attached to the van.
      // The upload runs server-side now; a failure here is REPORTED, not
      // swallowed — silently discarding it is how the first van ended up with
      // a registration on file that wasn't actually stored anywhere.
      let docWarning = null;
      if (pendingDoc) {
        try {
          await callFleet({
            action: "saveDoc", vehicle_id: j.vehicle.id,
            doc_type: pendingDoc.parsed.doc_type || "registration",
            title: pendingDoc.parsed.title || pendingDoc.file.name,
            file_name: pendingDoc.file.name, fileBase64: pendingDoc.base64,
            mime_type: pendingDoc.file.type, file_size: pendingDoc.file.size,
            issued_date: pendingDoc.parsed.issued_date || null,
            expiry_date: pendingDoc.parsed.expiry_date || null,
            extracted: pendingDoc.parsed,
          });
        } catch (e) {
          docWarning = `The vehicle saved, but the document didn't: ${e.message}. Re-drop it on the vehicle's Documents panel.`;
        }
      }
      onSaved(j.vehicle, { seeded: j.seeded, seedBasis: j.seedBasis, vinWarnings: j.vinWarnings, docWarning });
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button style={S.btn} onClick={onCancel}>← Back</button>
        <h1 style={S.h1}>Add a vehicle</h1>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Start from a document</div>
        <DropZone onFiles={onFiles} busy={busy}
                  label="Drop the vehicle registration here"
                  hint="Photo or PDF. Claude reads it and fills in the form below — insurance papers work too." />
        {extractNote && <div style={{ ...S.note, marginTop: 10 }}>{extractNote}</div>}
        <IssueList issues={issues} />
      </div>

      {err && <div style={S.err}>{err}</div>}

      <div style={S.card}>
        <div style={S.sectionH}>Identity</div>
        <div style={S.grid()}>
          <Field label="Our name for it" hint="e.g. Van 1"><input style={S.input} value={form.unit_name} onChange={set("unit_name")} placeholder="Van 1" /></Field>
          <Field label="Year"><input style={S.input} value={form.year} onChange={set("year")} inputMode="numeric" /></Field>
          <Field label="Make"><input style={S.input} value={form.make} onChange={set("make")} /></Field>
          <Field label="Model"><input style={S.input} value={form.model} onChange={set("model")} /></Field>
          <Field label="Body style"><input style={S.input} value={form.body_style} onChange={set("body_style")} /></Field>
          <Field label="Colour"><input style={S.input} value={form.colour} onChange={set("colour")} /></Field>
          <VinField
            value={form.vin} onChange={set("vin")} year={form.year}
            onDecoded={(f, vin) => setForm((prev) => {
              const next = { ...prev, vin };
              // Decode fills gaps; anything already typed wins.
              for (const [k, v2] of Object.entries(f)) if (next[k] === "" || next[k] == null) next[k] = v2;
              if (!next.qb_account_hint && f.make && f.model) next.qb_account_hint = `${f.make} ${f.model}`;
              if (!next.unit_name && f.make && f.model) next.unit_name = `${f.make} ${f.model}`;
              return next;
            })}
          />
          <Field label="Plate"><input style={S.input} value={form.plate} onChange={set("plate")} /></Field>
          <Field label="Engine" hint="From the VIN — not on the registration"><input style={S.input} value={form.engine} onChange={set("engine")} /></Field>
          <Field label="Drivetrain" hint="Decides which driveline items apply">
            <select style={S.input} value={form.drivetrain || ""} onChange={set("drivetrain")}>
              <option value="">— unknown —</option>
              <option value="RWD">RWD</option><option value="AWD">AWD</option><option value="FWD">FWD</option>
            </select>
          </Field>
          <Field label="Fuel type"><input style={S.input} value={form.fuel_type} onChange={set("fuel_type")} /></Field>
          <Field label="Registration #"><input style={S.input} value={form.registration_number} onChange={set("registration_number")} /></Field>
          <Field label="Certificate #"><input style={S.input} value={form.certificate_number} onChange={set("certificate_number")} /></Field>
          <Field label="VIC"><input style={S.input} value={form.vic} onChange={set("vic")} /></Field>
          <Field label="Net weight (kg)"><input style={S.input} value={form.net_weight_kg} onChange={set("net_weight_kg")} inputMode="numeric" /></Field>
          <Field label="GVW (kg)"><input style={S.input} value={form.gvw_kg} onChange={set("gvw_kg")} inputMode="numeric" /></Field>
          <Field label="Vehicle type"><input style={S.input} value={form.vehicle_type} onChange={set("vehicle_type")} /></Field>
          <Field label="Registration expiry"><input style={S.input} type="date" value={form.registration_expiry || ""} onChange={set("registration_expiry")} /></Field>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Who has it</div>
        <div style={S.grid(2)}>
          <Field label="Responsible employee" hint="Gets named on maintenance notices">
            <AssigneeSelect value={form.assigned_to_email} crew={crew} onChange={set("assigned_to_email")} />
          </Field>
          <Field label="Where it sits" hint="Whose house / yard it's parked at overnight">
            <input style={S.input} value={form.home_address} onChange={set("home_address")} placeholder="123 Example Rd, Salmon Arm" />
          </Field>
          <Field label="Current odometer (km)" hint="Starts the maintenance clock">
            <input style={S.input} value={form.odometer_km} onChange={set("odometer_km")} inputMode="numeric" />
          </Field>
          <Field label="Expected km/year" hint="Used to project km between readings">
            <input style={S.input} value={form.default_km_per_year} onChange={set("default_km_per_year")} inputMode="numeric" />
          </Field>
        </div>
      </div>

      <FinanceFields form={form} set={set} />

      <div style={S.card}>
        <div style={S.sectionH}>Expense matching</div>
        <div style={{ ...S.note, marginBottom: 10 }}>
          How we spot this van's maintenance in supplier invoices. The QB account hint is the strongest signal —
          your chart of accounts already books repairs under a per-vehicle sub-account.
        </div>
        <div style={S.grid(2)}>
          <Field label="QB account hint" hint='Substring of the QuickBooks account, e.g. "Ford Transit"'>
            <input style={S.input} value={form.qb_account_hint} onChange={set("qb_account_hint")} />
          </Field>
          <Field label="Other aliases" hint="Comma-separated: nicknames or codes a shop might print">
            <input style={S.input} value={form.match_aliases} onChange={set("match_aliases")} placeholder="Transit, White van" />
          </Field>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Maintenance profile</div>
        <DutyConditions value={form.duty_conditions} onChange={(v) => setForm((f) => ({ ...f, duty_conditions: v }))} />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button style={{ ...S.btnPri, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save}>{busy ? "Saving…" : "Add vehicle"}</button>
        <button style={S.btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Shared by add + edit — the numbers Dylan fills in himself.
// Rendered only for admin/owner: route gating in Layout is nav-only and fails
// open, so this component does its own check rather than trusting the nav.
function FinanceFields({ form, set }) {
  const { isAdmin, isOwner } = usePermissions() || {};
  if (!(isAdmin || isOwner)) return null;
  return (
    <>
      <div style={S.card}>
        <div style={S.sectionH}>Payments &amp; ownership</div>
        <div style={S.grid()}>
          <Field label="Ownership">
            <select style={S.input} value={form.ownership_type || ""} onChange={set("ownership_type")}>
              <option value="">— pick —</option>
              <option value="owned">Owned outright</option>
              <option value="financed">Financed</option>
              <option value="leased">Leased</option>
            </select>
          </Field>
          <Field label="Monthly payment"><input style={S.input} value={form.monthly_payment ?? ""} onChange={set("monthly_payment")} inputMode="decimal" placeholder="0.00" /></Field>
          <Field label="Interest rate (%)"><input style={S.input} value={form.interest_rate ?? ""} onChange={set("interest_rate")} inputMode="decimal" placeholder="6.99" /></Field>
          <Field label="Term (months)"><input style={S.input} value={form.term_months ?? ""} onChange={set("term_months")} inputMode="numeric" /></Field>
          <Field label="Lender / lessor"><input style={S.input} value={form.lender || ""} onChange={set("lender")} /></Field>
          <Field label="Payments start"><input style={S.input} type="date" value={form.payments_start_date || ""} onChange={set("payments_start_date")} /></Field>
          <Field label="Payoff / lease end"><input style={S.input} type="date" value={form.payoff_date || ""} onChange={set("payoff_date")} /></Field>
          <Field label="Buyout / residual"><input style={S.input} value={form.buyout_amount ?? ""} onChange={set("buyout_amount")} inputMode="decimal" /></Field>
          <Field label="Purchase date"><input style={S.input} type="date" value={form.purchase_date || ""} onChange={set("purchase_date")} /></Field>
          <Field label="Purchase price"><input style={S.input} value={form.purchase_price ?? ""} onChange={set("purchase_price")} inputMode="decimal" /></Field>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: "#334155", cursor: "pointer" }}>
          <input type="checkbox" checked={!!form.is_paid_off} onChange={set("is_paid_off")} />
          Paid off
        </label>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Insurance</div>
        <div style={S.grid()}>
          <Field label="Insurer"><input style={S.input} value={form.insurer || ""} onChange={set("insurer")} placeholder="ICBC" /></Field>
          <Field label="Policy #"><input style={S.input} value={form.policy_number || ""} onChange={set("policy_number")} /></Field>
          <Field label="Monthly premium"><input style={S.input} value={form.insurance_monthly ?? ""} onChange={set("insurance_monthly")} inputMode="decimal" /></Field>
          <Field label="Renewal date"><input style={S.input} type="date" value={form.insurance_renewal || ""} onChange={set("insurance_renewal")} /></Field>
        </div>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Maintenance schedule table
// ══════════════════════════════════════════════════════════════════════════
function ScheduleTable({ schedule, odo, vehicle, onEdit, onReseed }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const start = (s) => { setEditing(s.id); setDraft({ interval_km: s.interval_km ?? "", interval_months: s.interval_months ?? "", last_done_km: s.last_done_km ?? "", last_done_date: s.last_done_date ?? "" }); };
  const commit = async (s) => {
    await onEdit({ id: s.id, interval_km: num(draft.interval_km), interval_months: num(draft.interval_months), last_done_km: num(draft.last_done_km), last_done_date: draft.last_done_date || null });
    setEditing(null);
  };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ ...S.sectionH, marginBottom: 0 }}>Maintenance schedule</div>
        <button style={S.btnGhost} onClick={onReseed} title="Re-add any manufacturer items missing from this list">Re-seed from template</button>
      </div>

      {/* The 2025 Transit silently landed on the generic template because the
          Ford one only covered 2015-2019. Nobody should book work off
          placeholder numbers without being told they're placeholders. */}
      {schedule.some((s) => /NOT from the manufacturer/i.test(s.source_note || "")) && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#854d0e", borderRadius: 7, padding: "9px 12px", fontSize: 12.5, marginBottom: 10 }}>
          ⚠ <strong>These intervals are a generic placeholder, not this vehicle's manufacturer schedule.</strong> No manufacturer template matched
          {" "}{[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || "this vehicle"}. Check the owner's manual and correct them before booking work off these dates.
        </div>
      )}

      {odo?.estimated && (
        <div style={{ ...S.note, marginBottom: 10 }}>
          Odometer is <strong>estimated</strong> at {fmtKm(odo.km)} — last actual reading was {fmtKm(odo.lastReadingKm)} on {odo.asOf} ({odo.basis}). Add a reading to sharpen the km-based items.
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Status</th>
              <th style={S.th}>Service</th>
              <th style={S.th}>Every</th>
              <th style={S.th}>Last done</th>
              <th style={S.th}>Next due</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((s) => {
              const ed = editing === s.id;
              const off = s.enabled === false;
              return (
                <tr key={s.id} style={{ background: off ? "#fcfcfd" : s.due.status === "overdue" ? "#fffbfb" : undefined, opacity: off ? 0.75 : 1 }}>
                  <td style={S.td}>
                    {off
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>Not tracked</span>
                      : <Chip status={s.due.status} />}
                  </td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, color: off ? "#64748b" : "#0f172a" }}>{s.service_name}</div>
                    {s.source_note && <div style={{ fontSize: 11, color: "#94a3b8" }}>{s.source_note}</div>}
                    {s.notes && <div title={s.notes} style={{ fontSize: 11, color: "#94a3b8", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}>{s.notes}</div>}
                  </td>
                  <td style={S.td}>
                    {ed ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <input style={{ ...S.input, width: 74, padding: "4px 6px" }} value={draft.interval_km} onChange={(e) => setDraft({ ...draft, interval_km: e.target.value })} placeholder="km" />
                        <input style={{ ...S.input, width: 54, padding: "4px 6px" }} value={draft.interval_months} onChange={(e) => setDraft({ ...draft, interval_months: e.target.value })} placeholder="mo" />
                      </div>
                    ) : (
                      <span style={{ color: "#475569" }}>
                        {[s.interval_km ? `${Number(s.interval_km).toLocaleString()} km` : null, s.interval_months ? `${s.interval_months} mo` : null].filter(Boolean).join(" / ") || "—"}
                      </span>
                    )}
                  </td>
                  <td style={S.td}>
                    {ed ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <input style={{ ...S.input, width: 74, padding: "4px 6px" }} value={draft.last_done_km} onChange={(e) => setDraft({ ...draft, last_done_km: e.target.value })} placeholder="km" />
                        <input style={{ ...S.input, width: 124, padding: "4px 6px" }} type="date" value={draft.last_done_date || ""} onChange={(e) => setDraft({ ...draft, last_done_date: e.target.value })} />
                      </div>
                    ) : (
                      <span style={{ color: s.last_done_date || s.last_done_km != null ? "#475569" : "#cbd5e1" }}>
                        {s.last_done_km != null ? fmtKm(s.last_done_km) : ""}
                        {s.last_done_km != null && s.last_done_date ? " · " : ""}
                        {s.last_done_date || (s.last_done_km == null ? "never" : "")}
                      </span>
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={{ color: "#475569" }}>
                      {s.due.next_due_km != null ? fmtKm(s.due.next_due_km) : ""}
                      {s.due.next_due_km != null && s.due.next_due_date ? " · " : ""}
                      {s.due.next_due_date || ""}
                      {s.due.next_due_km == null && !s.due.next_due_date ? "—" : ""}
                    </div>
                    <div style={{ fontSize: 11, color: s.due.status === "overdue" ? "#991b1b" : "#94a3b8" }}>{s.due.reason}</div>
                  </td>
                  <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {ed
                      ? <><button style={{ ...S.btnGhost, marginRight: 4 }} onClick={() => commit(s)}>Save</button><button style={S.btnGhost} onClick={() => setEditing(null)}>✕</button></>
                      : <>
                          <button style={{ ...S.btnGhost, marginRight: 4 }} onClick={() => start(s)}>Edit</button>
                          <button style={S.btnGhost} onClick={() => onEdit({ id: s.id, enabled: off })} title={off ? "Track this item — set an interval first" : "Stop tracking this item"}>
                            {off ? "Track" : "Mute"}
                          </button>
                        </>}
                  </td>
                </tr>
              );
            })}
            {!schedule.length && <tr><td style={{ ...S.td, color: "#94a3b8" }} colSpan={6}>No schedule yet — try “Re-seed from template”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Expense scan — find maintenance in invoices we already have
// ══════════════════════════════════════════════════════════════════════════
function ExpenseScan({ vehicleId, vehicles, onApplied }) {
  const [state, setState] = useState({ loading: false, res: null, err: null });
  const [odoFor, setOdoFor] = useState({});
  const [vehFor, setVehFor] = useState({});

  const scan = async () => {
    setState({ loading: true, res: null, err: null });
    try {
      const j = await callFleet({ action: "scanExpenses", days: 730, vehicle_id: null });
      setState({ loading: false, res: j, err: null });
    } catch (e) { setState({ loading: false, res: null, err: e.message }); }
  };

  const apply = async (p) => {
    try {
      await callFleet({ action: "applyProposal", proposal: p, vehicle_id: vehFor[p.invoice_id] || p.vehicle_id, odometer_km: num(odoFor[p.invoice_id]) });
      setState((s) => ({ ...s, res: { ...s.res, proposals: s.res.proposals.map((x) => (x.invoice_id === p.invoice_id ? { ...x, applied: true } : x)) } }));
      onApplied?.();
    } catch (e) { setState((s) => ({ ...s, err: e.message })); }
  };

  const props = (state.res?.proposals || []).filter((p) => !vehicleId || p.vehicle_id === vehicleId || p.needs_review);

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ ...S.sectionH, marginBottom: 0 }}>Find maintenance in expenses</div>
        <button style={S.btn} onClick={scan} disabled={state.loading}>{state.loading ? "Scanning…" : "Scan invoices"}</button>
      </div>
      <div style={{ ...S.note, marginBottom: 10 }}>
        Reads the invoices already in Expenses and picks out vehicle servicing — matched by the work described on
        the line items, then attributed to a van by QB account, plate/VIN, or who expensed it. Nothing is written
        until you hit Apply.
      </div>

      {state.err && <div style={S.err}>{state.err}</div>}

      {state.res && !props.length && (
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Scanned {state.res.scanned} invoices — no new maintenance found. ({state.res.skipped} skipped as fuel, job materials, or non-vehicle.)
        </div>
      )}

      {props.map((p) => (
        <div key={p.invoice_id} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 11, marginBottom: 8, background: p.applied ? "#f0fdf4" : "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{p.vendor} · {fmt$(p.cost)} · {p.service_date || "no date"}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>{(p.service_codes || []).join(", ")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              {p.applied
                ? <Chip status="ok">Logged</Chip>
                : p.needs_review ? <Chip status="due_soon">{p.review_reason}</Chip> : <Chip status="ok">conf {p.confidence}</Chip>}
            </div>
          </div>

          {(p.signals?.length > 0 || p.reasons?.length > 0) && (
            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 7, lineHeight: 1.5 }}>
              {p.reasons?.length > 0 && <div><strong>Why maintenance:</strong> {p.reasons.join("; ")}</div>}
              {p.signals?.length > 0 && <div><strong>Why this van:</strong> {p.signals.join("; ")}</div>}
            </div>
          )}

          {!p.applied && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 9, flexWrap: "wrap" }}>
              <div style={{ minWidth: 160 }}>
                <label style={S.label}>Vehicle</label>
                <select style={S.input} value={vehFor[p.invoice_id] || p.vehicle_id || ""} onChange={(e) => setVehFor({ ...vehFor, [p.invoice_id]: e.target.value })}>
                  <option value="">— pick a vehicle —</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{vehName(v)}</option>)}
                </select>
              </div>
              <div style={{ width: 140 }}>
                <label style={S.label}>Odometer (optional)</label>
                <input style={S.input} value={odoFor[p.invoice_id] || ""} onChange={(e) => setOdoFor({ ...odoFor, [p.invoice_id]: e.target.value })} placeholder="km on the invoice" inputMode="numeric" />
              </div>
              <button style={{ ...S.btnPri, opacity: (vehFor[p.invoice_id] || p.vehicle_id) ? 1 : 0.5 }}
                      disabled={!(vehFor[p.invoice_id] || p.vehicle_id)} onClick={() => apply(p)}>Apply</button>
              {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" style={{ ...S.btnGhost, textDecoration: "none", lineHeight: "20px" }}>Receipt ↗</a>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Documents
// ══════════════════════════════════════════════════════════════════════════
function DocsPanel({ vehicleId, docs, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Upload goes through the API (server-side, service role) — the browser can't
  // write the private fleet-docs bucket, and pretending otherwise is what made
  // the first van's registration vanish. See api/fleet.js storageUpload().
  const onFiles = async (files) => {
    setBusy(true); setErr(null);
    try {
      for (const file of files) {
        const b64 = await readFileAsBase64(file);
        let parsed = {};
        try {
          const j = await callFleet({ action: "extractDoc", fileBase64: b64, mimeType: file.type, fileName: file.name });
          if (j.ok) parsed = j.parsed || {};
        } catch { /* an unreadable doc is still worth filing */ }

        await callFleet({
          action: "saveDoc", vehicle_id: vehicleId, doc_type: parsed.doc_type || "other",
          title: parsed.title || file.name, file_name: file.name,
          fileBase64: b64, mime_type: file.type, file_size: file.size,
          issued_date: parsed.issued_date || null, expiry_date: parsed.expiry_date || null, extracted: parsed,
        });
      }
      onChange();
    } catch (e) { setErr(`Couldn't save that document: ${e.message}`); } finally { setBusy(false); }
  };

  const open = async (d) => {
    try {
      const j = await callFleet({ action: "docUrl", id: d.id });
      window.open(j.url, "_blank");
    } catch (e) { setErr(e.message); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.title || d.file_name}"? This removes the file too.`)) return;
    try {
      await callFleet({ action: "deleteDoc", id: d.id });
      onChange();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div style={S.card}>
      <div style={S.sectionH}>Documents</div>
      <DropZone onFiles={onFiles} busy={busy} label="Drop insurance, registration, or service papers here" hint="Claude reads each one and files it by type" />
      {err && <div style={{ ...S.err, marginTop: 10, marginBottom: 0 }}>{err}</div>}

      {docs.length > 0 && (
        <table style={{ ...S.table, marginTop: 12 }}>
          <thead><tr><th style={S.th}>Type</th><th style={S.th}>Title</th><th style={S.th}>Expires</th><th style={S.th}></th></tr></thead>
          <tbody>
            {docs.map((d) => {
              const exp = d.expiry_date && new Date(d.expiry_date) < new Date();
              return (
                <tr key={d.id}>
                  <td style={S.td}><span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>{d.doc_type}</span></td>
                  <td style={S.td}>{d.title || d.file_name}</td>
                  <td style={S.td}>{d.expiry_date ? <span style={{ color: exp ? "#991b1b" : "#475569", fontWeight: exp ? 700 : 400 }}>{d.expiry_date}{exp ? " · expired" : ""}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                  <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...S.btnGhost, marginRight: 4 }} onClick={() => open(d)}>Open</button>
                    <button style={S.btnGhost} onClick={() => del(d)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Unassigned receipts — maintenance we've paid for that isn't on a van yet
// ══════════════════════════════════════════════════════════════════════════
const KIND_META = {
  needs_vehicle: { label: "Needs a vehicle", chip: "due" },
  matched:       { label: "Ready to file",   chip: "ok" },
  unrecognised:  { label: "Unclear",         chip: "due_soon" },
};

function ReceiptsQueue({ vehicles, onApplied }) {
  const [q, setQ] = useState("");
  const [state, setState] = useState({ loading: true, res: null, err: null });
  const [pick, setPick] = useState({});
  const [odo, setOdo] = useState({});

  const load = useCallback(async (text) => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const j = await callFleet({ action: "unassignedReceipts", q: text || "", days: 730 });
      setState({ loading: false, res: j, err: null });
    } catch (e) { setState({ loading: false, res: null, err: e.message }); }
  }, []);

  useEffect(() => { load(""); }, [load]);

  const file = async (r) => {
    const vid = pick[r.invoice_id] || r.vehicle_id;
    if (!vid) return;
    try {
      await callFleet({
        action: "applyProposal",
        proposal: { ...r, vehicle_id: vid },
        vehicle_id: vid,
        odometer_km: num(odo[r.invoice_id]),
      });
      setState((s) => ({ ...s, res: { ...s.res, receipts: s.res.receipts.filter((x) => x.invoice_id !== r.invoice_id) } }));
      onApplied?.();
    } catch (e) { setState((s) => ({ ...s, err: e.message })); }
  };

  const res = state.res;
  return (
    <div>
      <div style={{ ...S.card, marginBottom: 12 }}>
        <div style={S.sectionH}>Maintenance receipts not on a van yet</div>
        <div style={{ ...S.note, marginBottom: 10 }}>
          Every invoice in Expenses that looks like vehicle work but isn't logged against a vehicle — including the ones
          the matcher couldn't place. This is where a receipt goes to hide.
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input style={S.input} value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load(q)}
                 placeholder="Search supplier, invoice # or line items — e.g. “kal tire”, “oil”, “alignment”" />
          <button style={S.btn} onClick={() => load(q)}>Search</button>
        </div>
        {res && (
          <div style={{ display: "flex", gap: 14, marginTop: 11, fontSize: 12.5, flexWrap: "wrap" }}>
            <span><strong>{res.counts.needs_vehicle}</strong> <span style={{ color: "#64748b" }}>need a vehicle</span></span>
            <span><strong>{res.counts.matched}</strong> <span style={{ color: "#64748b" }}>ready to file</span></span>
            <span><strong>{res.counts.unrecognised}</strong> <span style={{ color: "#64748b" }}>unclear</span></span>
            <span style={{ marginLeft: "auto", color: "#94a3b8" }}>{res.scanned} invoices scanned</span>
          </div>
        )}
      </div>

      {state.err && <div style={S.err}>{state.err}</div>}
      {state.loading && <div style={{ padding: 24, color: "#94a3b8" }}>Searching invoices…</div>}
      {res && !res.receipts.length && !state.loading && (
        <div style={{ ...S.card, color: "#64748b", fontSize: 13 }}>
          Nothing unassigned{q ? ` matching “${q}”` : ""}. Every maintenance receipt we can recognise is already on a van.
        </div>
      )}

      {(res?.receipts || []).map((r) => {
        const meta = KIND_META[r.kind] || KIND_META.unrecognised;
        const vid = pick[r.invoice_id] || r.vehicle_id || "";
        return (
          <div key={r.invoice_id} style={{ ...S.card, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {r.vendor || "Unknown supplier"} · {fmt$(r.cost)}
                  <span style={{ color: "#94a3b8", fontWeight: 400 }}> · {r.service_date || "no date"}{r.invoice_number ? ` · #${r.invoice_number}` : ""}</span>
                </div>
                {r.service_codes?.length > 0 && (
                  <div style={{ fontSize: 12.5, color: "#334155", marginTop: 3 }}>{r.service_codes.join(", ")}</div>
                )}
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>{r.why}</div>
              </div>
              <Chip status={meta.chip}>{meta.label}</Chip>
            </div>

            {r.lines?.length > 0 && (
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 8, lineHeight: 1.5, borderTop: "1px solid #f1f5f9", paddingTop: 7 }}>
                {r.lines.map((l, i) => <div key={i}>· {l}</div>)}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 170 }}>
                <label style={S.label}>File against</label>
                <select style={S.input} value={vid} onChange={(e) => setPick({ ...pick, [r.invoice_id]: e.target.value })}>
                  <option value="">— pick a vehicle —</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{vehName(v)}</option>)}
                </select>
              </div>
              <div style={{ width: 150 }}>
                <label style={S.label}>Odometer (optional)</label>
                <input style={S.input} value={odo[r.invoice_id] || ""} inputMode="numeric"
                       onChange={(e) => setOdo({ ...odo, [r.invoice_id]: e.target.value })}
                       placeholder="km on the invoice" />
              </div>
              <button style={{ ...S.btnPri, opacity: vid && r.kind !== "unrecognised" ? 1 : 0.5 }}
                      disabled={!vid || r.kind === "unrecognised"}
                      title={r.kind === "unrecognised" ? "No service line was recognised — log this one by hand from the vehicle page" : ""}
                      onClick={() => file(r)}>
                File it
              </button>
              {r.receipt_url && <a href={r.receipt_url} target="_blank" rel="noreferrer" style={{ ...S.btnGhost, textDecoration: "none", lineHeight: "20px" }}>Receipt ↗</a>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Ask — chat over the fleet
// ══════════════════════════════════════════════════════════════════════════
const SUGGESTIONS = [
  "What's due across the fleet?",
  "Find maintenance receipts that aren't on a van yet",
  "What has Kal Tire done to the vans this year, and what did it cost?",
  "Which services have no baseline, and what do you need from me?",
];

function FleetAsk({ onChanged }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const next = [...msgs, { role: "user", content: q }];
    setMsgs(next); setInput(""); setBusy(true); setErr(null);
    try {
      const j = await callFleet({ action: "chat", messages: next });
      if (!j.ok) throw new Error(j.error || "chat failed");
      setMsgs([...next, { role: "assistant", content: j.text, calls: j.calls || [] }]);
      // Tool calls may have written — refresh the vehicle list behind us.
      if ((j.calls || []).some((c) => ["assign_receipt", "log_service", "set_baseline", "add_odometer"].includes(c.name))) onChanged?.();
    } catch (e) {
      setErr(e.message);
      setMsgs(next);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Ask about the fleet</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
          Reads the real records — schedules, service history, invoices. It can file receipts and set baselines when you ask.
        </div>
      </div>

      <div style={{ padding: 16, minHeight: 260, maxHeight: 520, overflowY: "auto", background: "#fcfcfd" }}>
        {!msgs.length && (
          <div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 9 }}>Try:</div>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} style={{ ...S.btnGhost, display: "block", width: "100%", textAlign: "left", marginBottom: 6, padding: "8px 11px", fontSize: 13 }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 14, display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "86%", padding: "9px 12px", borderRadius: 10, fontSize: 13.5, lineHeight: 1.55,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
              background: m.role === "user" ? "#a5ce3a" : "#fff",
              color: m.role === "user" ? "#1a2e05" : "#0f172a",
              border: m.role === "user" ? "none" : "1px solid #e2e8f0",
            }}>
              {m.content}
              {m.calls?.length > 0 && (
                <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 7, borderTop: "1px solid #f1f5f9", paddingTop: 5 }}>
                  looked at: {[...new Set(m.calls.map((c) => c.name))].join(", ")}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div style={{ fontSize: 13, color: "#94a3b8" }}>Thinking…</div>}
        {err && <div style={S.err}>{err}</div>}
        <div ref={endRef} />
      </div>

      <div style={{ display: "flex", gap: 6, padding: 12, borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <input style={S.input} value={input} onChange={(e) => setInput(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
               placeholder="Ask about maintenance, receipts, costs…" disabled={busy} />
        <button style={{ ...S.btnPri, opacity: busy || !input.trim() ? 0.5 : 1 }} disabled={busy || !input.trim()} onClick={() => send()}>Send</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Vehicle detail
// ══════════════════════════════════════════════════════════════════════════
function VehicleDetail({ id, crew, vehicles, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveNotes, setSaveNotes] = useState([]);
  const [odoDraft, setOdoDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const j = await callFleet({ action: "get", id });
      setData(j);
      setForm({
        ...j.vehicle,
        match_aliases: (j.vehicle.match_aliases || []).join(", "),
      });
    } catch (e) { setErr(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true); setErr(null); setSaveNotes([]);
    try {
      const j = await callFleet({
        action: "saveVehicle", ...form,
        year: num(form.year), gvw_kg: num(form.gvw_kg), net_weight_kg: num(form.net_weight_kg),
        default_km_per_year: num(form.default_km_per_year), purchase_price: num(form.purchase_price),
        monthly_payment: num(form.monthly_payment), interest_rate: num(form.interest_rate),
        term_months: num(form.term_months), buyout_amount: num(form.buyout_amount),
        insurance_monthly: num(form.insurance_monthly),
        match_aliases: String(form.match_aliases || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      setSaveNotes(j.vinWarnings || []);
      await load(); onChanged?.();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const addOdo = async () => {
    if (!odoDraft) return;
    try {
      await callFleet({ action: "addOdometer", vehicle_id: id, reading_km: num(odoDraft) });
      setOdoDraft(""); await load(); onChanged?.();
    } catch (e) { setErr(e.message); }
  };

  if (err && !data) return <div><button style={S.btn} onClick={onBack}>← Back</button><div style={{ ...S.err, marginTop: 12 }}>{err}</div></div>;
  if (!data || !form) return <div style={{ padding: 30, color: "#94a3b8" }}>Loading…</div>;

  const { vehicle, odo, schedule, logs, docs } = data;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <button style={S.btn} onClick={onBack}>← Fleet</button>
        <div>
          <h1 style={S.h1}>{vehName(vehicle)}</h1>
          <div style={S.sub}>
            {vehicle.plate ? `Plate ${vehicle.plate}` : "No plate"}{vehicle.vin ? ` · VIN ${vehicle.vin}` : ""}
          </div>
        </div>
      </div>

      {err && <div style={S.err}>{err}</div>}

      {/* Odometer */}
      <div style={S.card}>
        <div style={S.sectionH}>Odometer</div>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
              {odo.km == null ? "—" : fmtKm(odo.km)}
              {odo.estimated && <span style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}> est.</span>}
            </div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>
              {odo.km == null ? "No readings yet — add one to start tracking" : odo.estimated ? `Last read ${fmtKm(odo.lastReadingKm)} on ${odo.asOf} · ${odo.basis}` : `Read ${odo.asOf}`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginLeft: "auto" }}>
            <div style={{ width: 140 }}>
              <label style={S.label}>New reading</label>
              <input style={S.input} value={odoDraft} onChange={(e) => setOdoDraft(e.target.value)} placeholder="km" inputMode="numeric" />
            </div>
            <button style={S.btnPri} onClick={addOdo}>Add</button>
          </div>
        </div>
      </div>

      <ScheduleTable
        schedule={schedule} odo={odo} vehicle={vehicle}
        onEdit={async (patch) => { await callFleet({ action: "saveSchedule", ...patch }); await load(); onChanged?.(); }}
        // replace:true — without it this only ADDED missing items and reported
        // "already seeded", so the workflow the UI tells you to use (change the
        // duty conditions, then re-seed) silently did nothing. Baselines, muted
        // items and hand-edited intervals are preserved server-side.
        onReseed={async () => {
          const j = await callFleet({ action: "seedSchedule", vehicle_id: id, replace: true });
          await load(); onChanged?.();
          const bits = [];
          if (j.seeded) bits.push(`${j.seeded} item${j.seeded > 1 ? "s" : ""} added`);
          if (j.updated) bits.push(`${j.updated} interval${j.updated > 1 ? "s" : ""} refreshed`);
          if (j.keptCustom) bits.push(`${j.keptCustom} left alone (you'd edited them)`);
          setSaveNotes([bits.length ? `Re-seeded from “${j.template}” — ${bits.join(", ")}.` : `Already matches “${j.template}” — nothing to change.`]);
        }}
      />

      <ExpenseScan vehicleId={id} vehicles={vehicles} onApplied={() => { load(); onChanged?.(); }} />

      {/* Service history */}
      <div style={S.card}>
        <div style={S.sectionH}>Service history</div>
        {logs.length ? (
          <table style={S.table}>
            <thead><tr><th style={S.th}>Date</th><th style={S.th}>Odometer</th><th style={S.th}>Vendor</th><th style={S.th}>Work</th><th style={S.th}>Cost</th><th style={S.th}>Source</th><th style={S.th}></th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td style={S.td}>{l.service_date || "—"}</td>
                  <td style={S.td}>{l.odometer_km != null ? fmtKm(l.odometer_km) : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                  <td style={S.td}>{l.vendor || "—"}</td>
                  <td style={{ ...S.td, maxWidth: 320 }}>
                    <div style={{ fontSize: 12 }}>{(l.service_codes || []).join(", ")}</div>
                    {l.description && <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.description}</div>}
                  </td>
                  <td style={S.td}>{fmt$(l.cost)}</td>
                  <td style={S.td}><span style={{ fontSize: 11, color: "#64748b" }}>{l.source}{l.confidence != null ? ` ${l.confidence}` : ""}</span></td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <button style={S.btnGhost} onClick={async () => {
                      if (!window.confirm("Delete this service record?")) return;
                      await callFleet({ action: "deleteLog", id: l.id }); await load(); onChanged?.();
                    }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nothing logged yet — run the expense scan above, or add a record by hand.</div>}
      </div>

      <DocsPanel vehicleId={id} docs={docs} onChange={load} />

      {/* Editable details */}
      <div style={S.card}>
        <div style={S.sectionH}>Identity</div>
        <div style={S.grid()}>
          <Field label="Our name for it"><input style={S.input} value={form.unit_name || ""} onChange={set("unit_name")} /></Field>
          <Field label="Year"><input style={S.input} value={form.year ?? ""} onChange={set("year")} inputMode="numeric" /></Field>
          <Field label="Make"><input style={S.input} value={form.make || ""} onChange={set("make")} /></Field>
          <Field label="Model"><input style={S.input} value={form.model || ""} onChange={set("model")} /></Field>
          <Field label="Plate"><input style={S.input} value={form.plate || ""} onChange={set("plate")} /></Field>
          <VinField
            value={form.vin} onChange={set("vin")} year={form.year}
            onDecoded={(f, vin) => setForm((prev) => {
              const next = { ...prev, vin };
              for (const [k, v2] of Object.entries(f)) if (next[k] === "" || next[k] == null) next[k] = v2;
              if (!next.qb_account_hint && f.make && f.model) next.qb_account_hint = `${f.make} ${f.model}`;
              return next;
            })}
          />
          <Field label="Engine"><input style={S.input} value={form.engine || ""} onChange={set("engine")} /></Field>
          <Field label="Drivetrain">
            <select style={S.input} value={form.drivetrain || ""} onChange={set("drivetrain")}>
              <option value="">— unknown —</option>
              <option value="RWD">RWD</option><option value="AWD">AWD</option><option value="FWD">FWD</option>
            </select>
          </Field>
          <Field label="Registration expiry" hint="BC registrations often don't print one — leave blank if unsure">
            <input style={S.input} type="date" value={form.registration_expiry || ""} onChange={set("registration_expiry")} />
          </Field>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Who has it</div>
        <div style={S.grid(2)}>
          <Field label="Responsible employee">
            <AssigneeSelect value={form.assigned_to_email} crew={crew} onChange={set("assigned_to_email")} />
          </Field>
          <Field label="Where it sits"><input style={S.input} value={form.home_address || ""} onChange={set("home_address")} /></Field>
          <Field label="Expected km/year"><input style={S.input} value={form.default_km_per_year ?? ""} onChange={set("default_km_per_year")} inputMode="numeric" /></Field>
          <Field label="Status">
            <select style={S.input} value={form.status || "active"} onChange={set("status")}>
              <option value="active">Active</option>
              <option value="out_of_service">Out of service</option>
              <option value="sold">Sold</option>
            </select>
          </Field>
        </div>
      </div>

      <FinanceFields form={form} set={set} />

      <div style={S.card}>
        <div style={S.sectionH}>Expense matching</div>
        <div style={S.grid(2)}>
          <Field label="QB account hint"><input style={S.input} value={form.qb_account_hint || ""} onChange={set("qb_account_hint")} /></Field>
          <Field label="Other aliases"><input style={S.input} value={form.match_aliases || ""} onChange={set("match_aliases")} /></Field>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Maintenance profile</div>
        <DutyConditions value={form.duty_conditions} onChange={(v) => setForm((f) => ({ ...f, duty_conditions: v }))} />
      </div>

      <div style={S.card}>
        <div style={S.sectionH}>Notes</div>
        <textarea style={{ ...S.input, minHeight: 70, resize: "vertical", fontFamily: "inherit" }} value={form.notes || ""} onChange={set("notes")} />
      </div>

      <IssueList issues={saveNotes} />

      <div style={{ display: "flex", gap: 8, position: "sticky", bottom: 12, marginTop: 12 }}>
        <button style={{ ...S.btnPri, boxShadow: "0 2px 10px rgba(0,0,0,.12)", opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Root
// ══════════════════════════════════════════════════════════════════════════
/** Turn a saveVehicle response into the things a human needs told. */
function buildSaveNotice(info) {
  if (!info) return [];
  const out = [];
  for (const w of info.vinWarnings || []) out.push(w);
  if (info.docWarning) out.push(info.docWarning);
  // The 2025 Transit fell to the generic template silently. Never again.
  if (info.seedBasis?.generic) {
    out.push(`No manufacturer maintenance template matched this vehicle, so it was seeded with ${info.seeded} GENERIC placeholder intervals — these are NOT manufacturer data. Check the owner's manual and correct them on the schedule below before booking work off them.`);
  }
  return out;
}

export default function FleetTracker() {
  const perms = usePermissions() || {};
  const { isAdmin, isOwner } = perms;
  const [view, setView] = useState({ name: "list" });
  const [tab, setTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const j = await callFleet({ action: "list" });
      setVehicles(j.vehicles || []);
      setErr(null);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.from("crew_members").select("email,display_name").eq("is_active", true).order("display_name")
      .then(({ data }) => setCrew(data || []));
  }, []);

  const totals = useMemo(() => {
    const t = { overdue: 0, due: 0, due_soon: 0, monthly: 0 };
    for (const v of vehicles) {
      if (v.status !== "active") continue;
      t.overdue += v.counts?.overdue || 0;
      t.due += v.counts?.due || 0;
      t.due_soon += v.counts?.due_soon || 0;
      if (isAdmin || isOwner) t.monthly += Number(v.monthly_payment || 0) + Number(v.insurance_monthly || 0);
    }
    return t;
  }, [vehicles, isAdmin, isOwner]);

  if (view.name === "add") {
    return (
      <div style={S.wrap}>
        <AddVehicle crew={crew} onCancel={() => setView({ name: "list" })}
                    onSaved={(v, info) => { load(); setNotice(buildSaveNotice(info)); setView({ name: "detail", id: v.id }); }} />
      </div>
    );
  }
  if (view.name === "detail") {
    return (
      <div style={S.wrap}>
        {notice.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <IssueList issues={notice} />
            <button style={{ ...S.btnGhost, marginTop: 6 }} onClick={() => setNotice([])}>Dismiss</button>
          </div>
        )}
        <VehicleDetail id={view.id} crew={crew} vehicles={vehicles}
                       onBack={() => { setView({ name: "list" }); setNotice([]); load(); }} onChanged={load} />
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div>
          <h1 style={S.h1}>Fleet</h1>
          <div style={S.sub}>
            Vans, their papers, and manufacturer maintenance schedules. Taylor is emailed automatically when work comes due.
          </div>
        </div>
        {(isAdmin || isOwner) && <button style={S.btnPri} onClick={() => setView({ name: "add" })}>+ Add vehicle</button>}
      </div>

      {err && <div style={S.err}>{err}</div>}

      <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "1px solid #e2e8f0" }}>
        {[
          { k: "vehicles", label: "Vehicles" },
          { k: "receipts", label: "Unassigned receipts" },
          { k: "ask", label: "Ask" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: "8px 14px", border: "none", background: "none", cursor: "pointer",
            fontSize: 13.5, fontWeight: tab === t.k ? 800 : 600,
            color: tab === t.k ? "#0f172a" : "#64748b",
            borderBottom: `2px solid ${tab === t.k ? "#a5ce3a" : "transparent"}`, marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "receipts" && <ReceiptsQueue vehicles={vehicles} onApplied={load} />}
      {tab === "ask" && <FleetAsk onChanged={load} />}

      {tab === "vehicles" && <>
      {(totals.overdue > 0 || totals.due > 0) && (
        <div style={{ ...S.card, background: totals.overdue ? "#fffbfb" : "#fff", borderColor: totals.overdue ? "#fecaca" : "#e2e8f0", display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          {totals.overdue > 0 && <div><div style={{ fontSize: 22, fontWeight: 800, color: "#991b1b" }}>{totals.overdue}</div><div style={{ fontSize: 11.5, color: "#64748b" }}>overdue</div></div>}
          {totals.due > 0 && <div><div style={{ fontSize: 22, fontWeight: 800, color: "#9a3412" }}>{totals.due}</div><div style={{ fontSize: 11.5, color: "#64748b" }}>due now</div></div>}
          {totals.due_soon > 0 && <div><div style={{ fontSize: 22, fontWeight: 800, color: "#854d0e" }}>{totals.due_soon}</div><div style={{ fontSize: 11.5, color: "#64748b" }}>due soon</div></div>}
          {totals.monthly > 0 && <div style={{ marginLeft: "auto", textAlign: "right" }}><div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{fmt$(totals.monthly)}</div><div style={{ fontSize: 11.5, color: "#64748b" }}>payments + insurance / mo</div></div>}
        </div>
      )}

      {loading && <div style={{ padding: 30, color: "#94a3b8" }}>Loading fleet…</div>}

      {!loading && !vehicles.length && (
        <div style={{ ...S.card, textAlign: "center", padding: "34px 20px" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>🚐</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>No vehicles yet</div>
          <div style={{ fontSize: 13, color: "#64748b", margin: "5px 0 14px" }}>Drag a vehicle registration in and the details fill themselves.</div>
          <button style={S.btnPri} onClick={() => setView({ name: "add" })}>+ Add your first vehicle</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 12 }}>
        {vehicles.map((v) => <VehicleCard key={v.id} v={v} onOpen={() => setView({ name: "detail", id: v.id })} />)}
      </div>
      </>}
    </div>
  );
}
